import React from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { ArrowRight, Lock } from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import ThymosinAlpha1ModularContent from './peptides/ThymosinAlpha1Modular';
import LL37ModularContent from './peptides/LL37Modular';
import IpamorelinModularContent from './peptides/IpamorelinModular';
import BPC157ModularContent from './peptides/BPC157Modular';
import AOD9604ModularContent from './peptides/AOD9604Modular';
import { peptideCategories } from '@/data/peptidesData';

// Define sidebar links based on peptide categories
const sidebarLinks = peptideCategories.map(category => ({
  title: category.name,
  id: category.id,
  peptides: category.peptides.map(peptide => ({
    title: peptide.name,
    id: peptide.id,
  }))
}));

export default function PeptideProtocolsModular() {
  const [activeCategory, setActiveCategory] = React.useState('');
  const [activePeptide, setActivePeptide] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  
  // Check if user is premium
  const { data: authData, isLoading: authLoading } = useQuery<{
    isAuthenticated: boolean;
    user?: {
      id: number;
      username: string;
      email: string;
      subscriptionStatus: string;
    };
  }>({
    queryKey: ['/api/auth/status'],
    retry: false,
  });
  
  // Check for all valid premium statuses including 'mentorship'
  const isPremium = !authLoading && 
    authData?.isAuthenticated && 
    (authData?.user?.subscriptionStatus?.toLowerCase() === 'active' || 
     authData?.user?.subscriptionStatus?.toLowerCase() === 'trialing' ||
     authData?.user?.subscriptionStatus?.toLowerCase() === 'mentorship');

  // Effect to handle initial values from URL (either hash or query params)
  React.useEffect(() => {
    // Only run once when the component mounts
    const setInitialValues = () => {
      // Check for query parameters first (from direct links in PremiumContent)
      const urlParams = new URLSearchParams(window.location.search);
      const peptideParam = urlParams.get('peptide');
      
      let categoryId = '';
      let peptideId = '';
      
      if (peptideParam) {
        console.log("Found peptide query param:", peptideParam);
        [categoryId, peptideId] = peptideParam.split('/');
      } else {
        // Fall back to hash-based navigation
        const hash = window.location.hash.substring(1);
        console.log("Initial hash:", hash);
        
        if (hash) {
          [categoryId, peptideId] = hash.split('/');
        }
      }
      
      // If we have a category from either source, try to use it
      if (categoryId && peptideCategories.length > 0) {
        // Check if the category exists
        if (peptideCategories.some(cat => cat.id === categoryId)) {
          console.log("Setting initial category:", categoryId);
          setActiveCategory(categoryId);
          
          // If there's a peptide ID, check if it exists in the category
          if (peptideId) {
            const category = peptideCategories.find(cat => cat.id === categoryId);
            if (category && category.peptides.some(pep => pep.id === peptideId)) {
              console.log("Setting initial peptide:", peptideId);
              setActivePeptide(peptideId);
              
              // Update URL hash to match the selection (for consistency)
              window.history.replaceState(null, '', `#${categoryId}/${peptideId}`);
              return; // We found and set both category and peptide
            }
          }
          
          // If only category is provided or peptide wasn't found, select first peptide in category
          const category = peptideCategories.find(cat => cat.id === categoryId);
          if (category && category.peptides.length > 0) {
            console.log("Setting first peptide in category:", category.peptides[0].id);
            setActivePeptide(category.peptides[0].id);
            
            // Update URL hash to match the selection
            window.history.replaceState(null, '', `#${categoryId}/${category.peptides[0].id}`);
            return; // We set the category and default peptide
          }
        }
      }
      
      // Default values if no valid category/peptide found
      if (peptideCategories.length > 0) {
        console.log("Setting default category and peptide");
        setActiveCategory(peptideCategories[0].id);
        if (peptideCategories[0].peptides.length > 0) {
          setActivePeptide(peptideCategories[0].peptides[0].id);
          
          // Update URL hash to match the default selection
          window.history.replaceState(
            null, 
            '', 
            `#${peptideCategories[0].id}/${peptideCategories[0].peptides[0].id}`
          );
        }
      }
    };

    setInitialValues();
  }, [peptideCategories]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Get active content - using our new modular components
  const getActiveContent = () => {
    if (peptideCategories.length === 0) return null;
    
    const category = peptideCategories.find(cat => cat.id === activeCategory);
    if (!category) return null;
    
    const peptide = category.peptides.find(pep => pep.id === activePeptide);
    if (!peptide) return null;
    
    // Return the appropriate modular component based on peptide ID
    switch(peptide.id) {
      case 'thymosin-alpha-1':
        return <ThymosinAlpha1ModularContent />;
      case 'll-37':
        return <LL37ModularContent />;
      case 'ipamorelin':
        return <IpamorelinModularContent />;
      case 'bpc-157':
        return <BPC157ModularContent />;
      case 'aod-9604':
        return <AOD9604ModularContent />;
      default:
        return <div className="p-8 text-center">
          <p className="text-gray-500 text-lg">Content for {peptide.name} is coming soon!</p>
        </div>;
    }
  };

  // Show premium content restriction if not premium
  if (!isPremium) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <div className="container mx-auto px-4 py-8 flex-grow">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-2">Premium Peptide Protocols</h1>
          <p className="text-xl text-[#7F8C8D] mb-6">
            Comprehensive clinical guides for using peptides in longevity and regenerative medicine practice
          </p>
          
          <div className="bg-gradient-to-r from-[#F8F9FA] to-white border border-gray-200 rounded-xl p-8 shadow-sm max-w-3xl mx-auto text-center">
            <div className="inline-block p-3 bg-yellow-100 rounded-full mb-4">
              <Lock className="h-8 w-8 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#2C3E50] mb-2">Premium Content</h2>
            <p className="text-[#7F8C8D] mb-6">
              This section contains premium peptide protocols and clinical guides available exclusively for members.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/membership">
                <Button size="lg" className="bg-[#3498DB] hover:bg-[#2980B9]">
                  Upgrade to Premium
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link href="/login?from=/peptide-protocols-modular">
                <Button variant="outline" size="lg">
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 flex-grow">
        <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-2">Premium Peptide Protocols</h1>
        <p className="text-xl text-[#7F8C8D] mb-8">
          Comprehensive clinical guides for using peptides in longevity and regenerative medicine practice
        </p>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Navigation sidebar */}
          <div className="col-span-1">
            <div className="sticky top-20">
              <div className="mb-6">
                <Input
                  type="search"
                  placeholder="Search protocols..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-4">
                {peptideCategories.length === 0 ? (
                  <p className="text-gray-500 italic">No peptide categories available yet</p>
                ) : (
                  peptideCategories.map((category) => (
                    <div key={category.id} className="space-y-2">
                      <h3 className="font-medium text-[#2C3E50]">{category.name}</h3>
                      <div className="ml-2 space-y-1">
                        {category.peptides.map((peptide) => (
                          <button
                            key={peptide.id}
                            onClick={() => {
                              console.log(`Selecting: ${category.id}/${peptide.id}`);
                              setActiveCategory(category.id);
                              setActivePeptide(peptide.id);
                              // Update the URL hash without triggering a navigation
                              window.history.replaceState(
                                null, 
                                '', 
                                `#${category.id}/${peptide.id}`
                              );
                            }}
                            className={`block w-full text-left py-1 px-2 rounded text-sm ${
                              activeCategory === category.id && activePeptide === peptide.id
                                ? 'bg-[#EBF5FB] text-[#3498DB] font-medium'
                                : 'text-[#7F8C8D] hover:bg-gray-100'
                            }`}
                          >
                            {peptide.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          
          {/* Main content area */}
          <div className="col-span-1 lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {peptideCategories.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-500 text-lg">Peptide protocols are coming soon!</p>
                  <p className="text-gray-400 mt-2">We're currently preparing comprehensive protocols for you.</p>
                </div>
              ) : getActiveContent()}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}